import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRight, Loader2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const HEARING_TYPES = [
  "Tier 2 Disciplinary",
  "Tier 3 Disciplinary",
  "Parole Hearing",
  "Initial Appearance",
  "Suppression Hearing",
  "Family Court",
  "Civil Hearing",
  "Other",
];

const EMPTY = {
  full_name: "", email: "", phone: "", din: "", facility: "",
  hearing_type: "", hearing_date: "", location: "",
  case_summary: "", goals: "", key_witnesses: "",
  documents_available: "", additional_notes: "",
};

export default function IntakeHearing() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/intake/hearing", form);
      setSubmitted(true);
      toast.success("Hearing intake submitted. We'll review and reach out within 1 business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="bg-black text-zinc-100 min-h-screen pt-16 pb-24">
        <div className="container-pl max-w-xl text-center" data-testid="hearing-intake-success">
          <CheckCircle2 className="h-12 w-12 text-amber-400 mx-auto mb-5" strokeWidth={1.5}/>
          <p className="overline mb-3 justify-center">Submission received</p>
          <h1 className="display-serif text-4xl text-zinc-50 mb-3 accent-rule inline-block">Thank you.</h1>
          <p className="text-zinc-300 mt-5 leading-relaxed">
            Your hearing-preparation intake is now in our review queue. We'll reach out within one business day with next steps.
          </p>
          <div className="mt-8">
            <Link to="/services"><Button variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30">Browse services <ArrowRight className="ml-2 h-4 w-4"/></Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-zinc-100 min-h-screen pt-12 pb-24">
      <div className="container-pl max-w-2xl">
        <p className="overline mb-4">Hearing Preparation · Free review</p>
        <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50 leading-tight mb-3 accent-rule">
          Hearing Intake
        </h1>
        <p className="text-base text-zinc-400 mb-8 max-w-xl leading-relaxed">
          Tell us about your upcoming hearing. We'll review the facts and respond with a structured prep plan — at no charge.
        </p>

        <div className="border border-amber-400/30 bg-blue-950/20 rounded-sm p-4 mb-8 flex gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-xs text-zinc-300 leading-relaxed">
            This intake is reviewed by our team — not legal advice. We are not attorneys. Submission goes directly to admin for review.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="hearing-intake-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-zinc-300">Full Name <span className="text-amber-400">*</span></Label><Input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-name"/></div>
            <div><Label className="text-zinc-300">Email <span className="text-amber-400">*</span></Label><Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-email"/></div>
            <div><Label className="text-zinc-300">Phone</Label><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-phone"/></div>
            <div><Label className="text-zinc-300">DIN (if applicable)</Label><Input value={form.din} onChange={(e) => update("din", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-din"/></div>
            <div className="sm:col-span-2"><Label className="text-zinc-300">Facility (if applicable)</Label><Input value={form.facility} onChange={(e) => update("facility", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-facility"/></div>
          </div>

          <div>
            <Label className="text-zinc-300">Hearing Type <span className="text-amber-400">*</span></Label>
            <Select value={form.hearing_type} onValueChange={(v) => update("hearing_type", v)} required>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5 text-zinc-100" data-testid="h-type"><SelectValue placeholder="Choose a hearing type"/></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {HEARING_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-zinc-300">Hearing Date (if scheduled)</Label><Input type="date" value={form.hearing_date} onChange={(e) => update("hearing_date", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-date"/></div>
            <div><Label className="text-zinc-300">Location</Label><Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Court/facility" className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-location"/></div>
          </div>

          <div><Label className="text-zinc-300">Case Summary <span className="text-amber-400">*</span></Label><Textarea required rows={5} value={form.case_summary} onChange={(e) => update("case_summary", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-summary" placeholder="Brief facts. What's the case about? What's at stake?"/></div>
          <div><Label className="text-zinc-300">Your Goals</Label><Textarea rows={3} value={form.goals} onChange={(e) => update("goals", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-goals" placeholder="What outcome are you hoping for?"/></div>
          <div><Label className="text-zinc-300">Key Witnesses</Label><Textarea rows={2} value={form.key_witnesses} onChange={(e) => update("key_witnesses", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-witnesses" placeholder="Names and roles"/></div>
          <div><Label className="text-zinc-300">Documents Available</Label><Textarea rows={2} value={form.documents_available} onChange={(e) => update("documents_available", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-docs" placeholder="What evidence/records do you have?"/></div>
          <div><Label className="text-zinc-300">Additional Notes</Label><Textarea rows={3} value={form.additional_notes} onChange={(e) => update("additional_notes", e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="h-notes"/></div>

          <div className="flex items-center justify-between pt-6 border-t border-zinc-800">
            <Link to="/services" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors">← Back to services</Link>
            <Button type="submit" size="lg" disabled={submitting} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="h-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : <>Submit for review <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5}/></>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
